import { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Button, Heading, Text, Card } from 'frosted-ui';
import Layout from './components/Layout';
import Home from './pages/Home';
import NewTestGate from './components/billing/NewTestGate';
import PathChooser from './pages/PathChooser';
import NoBuyersGuide from './pages/NoBuyersGuide';
import AddSpot from './pages/AddSpot';
import ExperimentWizard from './pages/ExperimentWizard';
import ExperimentDashboard from './pages/ExperimentDashboard';
import DevExperiments from './pages/DevExperiments';
import BuyPage from './pages/BuyPage';
import OfferPage from './pages/OfferPage';
import ThankYouPage from './pages/ThankYouPage';
import PrivacyPolicy from './pages/PrivacyPolicy';
import TermsOfService from './pages/TermsOfService';
import ExperienceOfferPage from './pages/ExperienceOfferPage';
import LoadingScreen from './components/LoadingScreen';
import { CompanyProvider } from './contexts/CompanyContext';
import { OnboardingProvider, useOnboarding } from './contexts/OnboardingContext';
import { FirstRunProvider } from './contexts/FirstRunContext';
import { TermsProvider } from './contexts/TermsContext';
import FirstRunFrame from './components/Onboarding/FirstRunFrame';
import { WelcomeRevenue, WelcomeResult } from './pages/Welcome';
import { api } from './services/api';
import { useDocumentTitle } from './hooks/useDocumentTitle';
import { isPublicPath } from './utils/publicRoutes';
import { loadGoogleAnalytics } from './utils/googleAnalytics';

/** Home, or first run for a company that hasn't finished (or skipped) it. */
function HomeOrWelcome() {
  const { isOnboardingComplete } = useOnboarding();
  return isOnboardingComplete ? <Home /> : <Navigate to="/welcome" replace />;
}

/** The dashboard routes. Only / sends to first run, so deep links still work. */
export function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<HomeOrWelcome />} />
      <Route path="/experiments" element={<Navigate to="/" replace />} />
      {/* First run's pages share one frame, so its header carries across them */}
      <Route element={<FirstRunFrame />}>
        <Route path="/welcome" element={<WelcomeRevenue />} />
        <Route path="/welcome/result" element={<WelcomeResult />} />
        <Route path="/new" element={<NewTestGate><PathChooser /></NewTestGate>} />
        <Route path="/new/no-buyers" element={<NoBuyersGuide />} />
        <Route path="/experiments/new" element={<NewTestGate><ExperimentWizard /></NewTestGate>} />
      </Route>
      <Route path="/experiments/:id" element={<ExperimentDashboard />} />
      <Route path="/experiments/:id/spots/new" element={<AddSpot />} />
      {/* Old Paid Posts creator pages: pay-to-unlock is a test now */}
      <Route path="/posts/*" element={<Navigate to="/" replace />} />
      {/* Dev-only route for viewing all experiments */}
      {import.meta.env.DEV && <Route path="/dev/experiments" element={<DevExperiments />} />}
    </Routes>
  );
}

function App() {
  const [userData, setUserData] = useState<Awaited<
    ReturnType<typeof api.getMe>
  > | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // The member-facing experience view (/experiences/) is authed by the Whop
  // user token per request but has no company dashboard context, so like the
  // other public pages it skips getMe below.
  // Public pages (no auth required): buyer, member and legal pages.
  const isPublicPage = isPublicPath(window.location.pathname);
  useDocumentTitle(isPublicPage ? null : 'Paywall Lab');

  useEffect(() => {
    // Skip auth (and Google Analytics) for public pages
    if (isPublicPage) {
      setLoading(false);
      return;
    }
    loadGoogleAnalytics();

    // Extract company ID from initial URL - this should only run once on mount
    const pathname = window.location.pathname;
    const search = window.location.search;

    // Try to get companyId from URL query params first
    const urlParams = new URLSearchParams(search);
    const companyIdFromQuery = urlParams.get('companyId');

    // Fallback to extracting from path (Dashboard view: /dashboard/biz_xxx[/...])
    const companyIdFromPath = pathname.match(/\/dashboard\/([^/]+)/)?.[1];

    const companyId = companyIdFromQuery || companyIdFromPath || undefined;

    // Fetch user data once with the extracted companyId
    api
      .getMe(companyId)
      .then((data) => {
        setUserData(data);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Failed to load the app:', err);
        setError(true);
        setLoading(false);
      });
  }, [isPublicPage]);

  // Public pages and the experience view - no dashboard auth
  if (isPublicPage) {
    return (
      <Routes>
        <Route path="/experiences/:experienceId" element={<ExperienceOfferPage />} />
        <Route path="/buy/:experimentId" element={<BuyPage />} />
        <Route path="/g/:companyRoute/:shareCode" element={<BuyPage />} />
        <Route path="/p/:companyRoute/:shareCode" element={<OfferPage />} />
        <Route path="/thank-you" element={<ThankYouPage />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
      </Routes>
    );
  }

  // Loading state
  if (loading) {
    return <LoadingScreen />;
  }

  // Error state
  if (error || !userData) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-background)',
        }}
      >
        <Card size="3" style={{ maxWidth: 400, textAlign: 'center' }}>
          <Heading size="5" style={{ marginBottom: 'var(--space-4)' }}>
            Couldn't open Paywall Lab
          </Heading>
          <Text size="2" color="gray" style={{ display: 'block', marginBottom: 'var(--space-4)' }}>
            If you've had this open a while, your session may have ended. Reload, or open the app
            again from your Whop dashboard.
          </Text>
          <Button variant="solid" onClick={() => window.location.reload()}>
            Reload
          </Button>
        </Card>
      </div>
    );
  }

  // Company context is required for the app to function
  if (!userData.company) {
    return (
      <div
        style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 'var(--space-4)',
          backgroundColor: 'var(--color-background)',
        }}
      >
        <Card size="3" style={{ maxWidth: 400, textAlign: 'center' }}>
          <Heading size="5" style={{ marginBottom: 'var(--space-4)' }}>
            Open Paywall Lab from Whop
          </Heading>
          <Text size="3" color="gray" style={{ marginBottom: 'var(--space-4)', display: 'block' }}>
            Paywall Lab runs inside your business dashboard on Whop.
          </Text>
          <Button asChild variant="solid">
            <a href="https://whop.com/dashboard" target="_top" rel="noreferrer">
              Open Whop dashboard
            </a>
          </Button>
        </Card>
      </div>
    );
  }

  // Main app
  return (
    <CompanyProvider companyId={userData.company.id} companyRoute={userData.company.route ?? null}>
      <TermsProvider
        initialStatus={{
          acceptedAt: userData.company.termsAcceptedAt ?? null,
          version: userData.company.termsVersion ?? null,
        }}
      >
        <OnboardingProvider initialStatus={userData.company.onboarding}>
          <FirstRunProvider>
            <Layout>
              <AppRoutes />
            </Layout>
          </FirstRunProvider>
        </OnboardingProvider>
      </TermsProvider>
    </CompanyProvider>
  );
}

export default App;
